import React from 'react';
import { Platform, ScrollView, useWindowDimensions } from 'react-native';
import { ANDROID_MARKET } from '@env';
import { Trans } from '@lingui/macro';

import Button from '@src/components/Button';
import { Pagination } from '@src/components/Carousel';
import Container from '@src/components/Container';
import Separator from '@src/components/Separator';
import SlideComponent from '@src/components/Slide';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { SubscriptionId } from '@src/enums';
import useBuyImmigrationPlans from '@src/hooks/useBuyImmigrationPlans';
import useBuyPlansCafeBazaar from '@src/hooks/useBuyPlansCafeBazaar';
import useCafeBazaar from '@src/hooks/useCafeBazaar';
import useRefreshImmigrationStatus from '@src/hooks/useRefreshImmigrationStatus';
import useSnackBar from '@src/hooks/useSnackBar';

import Plan from './Plan';
import {
  ContentSubtitle,
  ContentTitle,
  Controls,
  ImmigrationPlanList,
  Plans,
  PlansCarousel,
  Slides,
} from './styled';
import { Props } from './types';
import useSlides from './useSlides';

const Screen: React.FC<Props> = ({
  route: { params = { immigrationId: undefined, result: undefined } },
}) => {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const slides = useSlides();
  const { handleSubscribe: handleSubscribeCafeBazaar } = useCafeBazaar();
  const snackBar = useSnackBar();
  const dimensions = useWindowDimensions();
  const { itemWidth, sliderWidth } = React.useMemo(
    () => ({
      itemWidth: dimensions.width - 50,
      sliderWidth: dimensions.width,
    }),
    [dimensions.width]
  );

  const {
    activePlanIndex,
    activePlan,
    plans,
    handleSnapToPlan,
    payForImmigrationPlan,
    handleBuyPress,
    calculateIndex,
    // handleBuyPlan,
  } = useBuyImmigrationPlans(params.immigrationId, params.result);
  const {
    loading: bazaarLoading,
    makeRequest: payPlanFromCafeBazaar,
  } = useBuyPlansCafeBazaar();
  const refreshImmigrationStatus = useRefreshImmigrationStatus(true);

  // TODO: refactor this to simpler handler
  const buyImmigrationPlanHandler = async () => {
    if (Platform.OS === 'android') {
      switch (ANDROID_MARKET) {
        case 'cafeBazaar': {
          try {
            const purchaseResult = await handleSubscribeCafeBazaar(
              activePlan.days
            );
            if (purchaseResult) {
              // await handleBuyPlan(activePlan.id);
              await payPlanFromCafeBazaar(
                activePlan.id,
                purchaseResult.packageName,
                purchaseResult.purchaseToken,
                purchaseResult.productId as SubscriptionId
              );
              await refreshImmigrationStatus();
            }
          } catch (error) {
            if (error instanceof Error) {
              snackBar.show({ content: error.message });
            } else {
              snackBar.show({ content: error });
            }
          }
          break;
        }

        case 'playStore': {
          await handleBuyPress();
          await refreshImmigrationStatus();
          break;
        }

        default:
          await handleBuyPress();
          await refreshImmigrationStatus();
          break;
      }
    } else {
      await handleBuyPress();
      await refreshImmigrationStatus();
    }
  };

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <ScrollView>
          <Slides>
            <PlansCarousel
              firstItem={0}
              initialNumToRender={3}
              data={slides.data}
              itemWidth={itemWidth}
              sliderWidth={sliderWidth}
              onSnapToItem={setActiveSlide}
              renderItem={({ item: { cover } }) => (
                <SlideComponent onPress={() => null} source={cover.source} />
              )}
            />
            <Pagination
              dotsLength={slides.data.length}
              activeDotIndex={activeSlide}
            />
          </Slides>
          <Separator />
          <Plans>
            {plans.loading ? null : (
              <ImmigrationPlanList
                extraData={activePlanIndex}
                data={plans.data}
                renderItem={({ item, index }) => {
                  const newIndex = calculateIndex(index);

                  return (
                    <Plan
                      active={activePlanIndex === newIndex}
                      data={item}
                      onPress={() => handleSnapToPlan(newIndex)}
                    />
                  );
                }}
              />
            )}
          </Plans>

          <Controls>
            <ContentTitle>
              <Trans>Money-back guarantee</Trans>
            </ContentTitle>
            <ContentSubtitle>
              <Trans>
                if you are not satisfied with youtopin Immigration product or
                service, a refund will be made
              </Trans>
            </ContentSubtitle>
            <Separator />
            <Button
              mode="contained"
              onPress={buyImmigrationPlanHandler}
              loading={payForImmigrationPlan.loading || bazaarLoading}
              disabled={payForImmigrationPlan.loading || bazaarLoading}
            >
              <Trans>Buy Subscription</Trans>
            </Button>
          </Controls>
        </ScrollView>
      </Container>
    </>
  );
};

Screen.displayName = 'ImmigrationPlans';

export default React.memo(Screen);
